import React, { useState, ReactNode, useRef } from 'react';
import { Button, ButtonProps } from 'ALIASED-antd';

type ClickHandler =
  | ((e: React.MouseEvent<HTMLButtonElement>) => void)
  | ((e: React.MouseEvent<HTMLButtonElement>) => Promise<void>);

export interface AsyncAntdButtonProps extends Omit<ButtonProps, 'onClick'> {
  onClick?: ClickHandler;
  children?: ReactNode;
  initiallyDisabled?: boolean;
  ['data-testid']: string;
}

const AsyncAntdButton: React.FC<AsyncAntdButtonProps> = ({
  onClick,
  children,
  ['data-testid']: dataTestId,
  initiallyDisabled = false,
  ...rest
}) => {
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(initiallyDisabled);
  const timeoutRef = useRef<number | null>(null);

  const handleAsyncClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    try {
      if (onClick) {
        setButtonDisabled(true);

        timeoutRef.current = window.setTimeout(() => {
          setLoading(true);
        }, 300);

        await onClick(e);
      }
    } catch (error) {
      console.error('Error during async operation:', error);
    } finally {
      setLoading(false);
      setButtonDisabled(false);

      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  const childArray = React.Children.toArray(children).filter(Boolean);
  const hasContent = childArray.length > 0;

  if (!hasContent) {
    return (
      <Button
        onClick={handleAsyncClick}
        loading={loading}
        disabled={buttonDisabled || rest.disabled}
        {...rest}
        data-testid={dataTestId}
      />
    );
  }

  const computedGap = childArray.length > 1 ? '10px' : '0px';

  return (
    <Button
      onClick={handleAsyncClick}
      loading={loading}
      disabled={buttonDisabled || rest.disabled}
      {...rest}
      data-testid={dataTestId}
    >
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: computedGap,
          fontSize: '14px'
        }}
      >
        {children}
      </div>
    </Button>
  );
};

export default AsyncAntdButton;
